"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function SubProductsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => <div key={i} className="h-[500px] rounded-lg bg-muted animate-pulse" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post, i) => {
      const isPopular = post.customFields?.product_category?.toLowerCase().includes("destacado");
      const productImage = {
        src: post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/placeholder_steel.png",
        srcset: "",
        sizes: ""
      };
      return <div data-index={i} key={post.id} className="group relative flex flex-col bg-card border border-border rounded-lg p-10 transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-xl overflow-hidden">
            {isPopular && <Badge className="absolute top-6 right-6 bg-primary text-primary-foreground font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-md shadow-sm z-10">
                Destacado
              </Badge>}

            <div className="aspect-square w-full bg-muted rounded-lg flex items-center justify-center mb-8 overflow-hidden">
              <img src={productImage.src} data-wvc-srcset={productImage.srcset} data-wvc-sizes={productImage.sizes} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </div>

            <div className="mb-5 text-center">
              <h3 className="font-default text-lg font-semibold text-foreground mb-2 tracking-tight">
                {post.title}
              </h3>
              {post.customFields?.product_category && <p className="font-mono text-xs tracking-[0.2em] uppercase text-primary font-semibold">
                  {post.customFields.product_category}
                </p>}
            </div>

            <p className="font-default text-base text-muted-foreground leading-relaxed mb-6 line-clamp-3">
              {post.customFields?.descripcion}
            </p>

            <div className="relative h-px bg-primary/40 w-16 mb-6 transition-all duration-500 ease-out group-hover:w-full" />

            <div className="mt-auto flex flex-col gap-4">
              <Button asChild variant="link" className="p-0 h-auto font-mono text-xs tracking-[0.2em] uppercase text-foreground hover:text-primary no-underline hover:no-underline">
                <Link to={post.link || "/ProductsPage"} className="inline-flex items-center gap-2">
                  Ver Catálogo
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-primary text-white px-6 py-2 rounded-md hover:bg-secondary cursor-pointer min-w-[48px] min-h-[48px]" asChild>
                <Link to={post.link || "#"}>
                  Ver especificaciones
                </Link>
              </Button>
            </div>
          </div>;
    })}
    </div>;
}
export default function SubProductsGrid() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridContainer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const wp_query_params = {
    post_type: "subproducto",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="244" id="sub-products-grid" className="bg-background text-foreground pt-24 md:pt-32 lg:pt-40 pb-20 md:pb-24 lg:pb-30">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div ref={header.ref} className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-primary mb-6">
            Catálogo / Div. Acero
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] tracking-tight mb-6">
            Soluciones en <span className="text-secondary">Acero</span> de Calidad
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground max-w-[600px] mx-auto leading-relaxed">
            Desde cimientos hasta acabados finales — explore nuestras categorías principales y encuentre el material que impulsa su construcción.
          </p>
        </div>

        <div ref={gridContainer.ref} className={`transition-all duration-700 ease-out delay-150 ${gridContainer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <PostsProvider wp_query={{
          post_type: "subproducto",
          per_page: 6,
          order: "desc",
          orderby: "date"
        }}>
            <SubProductsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>;
}